import { RenderFormat, ViewerOpenContextKind, type ContentViewerSession, type FileContentRendererConfig } from '@/content-viewer/store/schemas'
import type { PaxtonHighlightArea } from '@/components/react-pdf-viewer/schema/paxton-highlight-area'
import { buildLegacyFileViewerSession } from '@/routes/dashboard/files/utils/build-legacy-file-viewer-session'
import { buildUserFileRendererConfig } from '@/user_files/files/utils/build-user-file-renderer-config'
import { buildUserFileViewerSession } from '@/user_files/files/utils/build-user-file-viewer-session'
import type { UserFileRead } from '@/uppy-file-upload/schemas'
import { type MedChronReference } from '@/medical-chronology/references/store/schemas'
import { MedChronSourceProvider, type MedChronSourceRef } from './schemas'

type BuildMedChronViewerSessionArgs = {
  source: MedChronSourceRef
  reference?: MedChronReference
  userFile?: UserFileRead | null
  highlights?: PaxtonHighlightArea[]
  startingPage?: number
}

function buildLegacyRenderer(
  source: Extract<MedChronSourceRef, { provider: MedChronSourceProvider.LEGACY }>,
  highlights?: PaxtonHighlightArea[],
  startingPage?: number
): FileContentRendererConfig {
  return {
    format: RenderFormat.PDF,
    filePath: source.file_path,
    ...(highlights ? { highlights } : {}),
    ...(startingPage !== undefined ? { startingPage } : {}),
  }
}

export function buildMedChronViewerSession(args: BuildMedChronViewerSessionArgs): ContentViewerSession | null {
  const { source, reference, userFile, highlights, startingPage } = args

  if (source.provider === MedChronSourceProvider.LEGACY) {
    const isReferenceBacked = Boolean(reference)
    return buildLegacyFileViewerSession({
      id: source.source_id,
      title: source.display_name,
      renderer: buildLegacyRenderer(source, highlights, isReferenceBacked ? undefined : startingPage),
      context: reference
        ? {
            kind: ViewerOpenContextKind.MED_CHRON_REFERENCE,
            referenceId: reference.id,
            sourceId: source.source_id,
          }
        : {
            kind: ViewerOpenContextKind.MED_CHRON_SOURCE,
            sourceId: source.source_id,
          },
      downloadable: !isReferenceBacked,
    })
  }

  if (!userFile) return null

  const baseRenderer = buildUserFileRendererConfig(userFile)
  if (!baseRenderer) return null

  // Med source highlighting is PDF-only today. Non-PDF sources need a separate
  // presentation primitive before highlights or startingPage can apply.
  const renderer =
    baseRenderer.format === RenderFormat.PDF
      ? {
          ...baseRenderer,
          ...(highlights ? { highlights } : {}),
          ...(startingPage !== undefined ? { startingPage } : {}),
        }
      : baseRenderer

  return buildUserFileViewerSession({
    userFile,
    renderer,
    context: reference
      ? {
          kind: ViewerOpenContextKind.MED_CHRON_REFERENCE,
          referenceId: reference.id,
          sourceId: source.source_id,
        }
      : {
          kind: ViewerOpenContextKind.MED_CHRON_SOURCE,
          sourceId: source.source_id,
        },
  })
}
